import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Building2, Calculator, ChartBar, CheckCircle, FileText, InfoIcon, Home, Wallet, ShieldCheck } from "lucide-react";
import { AsuntolainaCalculator } from "@/components/loan/AsuntolainaCalculator";
import KayttokohteetCard from "@/components/home/KayttokohteetCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoanBenefitsSection } from "@/components/loan/LoanBenefitsSection";
import { LainaCta } from "@/components/common/LainaCta";
import Image from "@/components/ui/image";
import { PartnersSection } from "@/components/loan/PartnersSection";

const AsuntolainaPage = () => {
  const benefits = [
    {
      icon: <Calculator className="h-6 w-6 text-blue-600" />,
      title: "Helppo laskea",
      description: "Käytä laskureita arvioidaksesi kuukausierän ja kokonaiskustannukset"
    },
    {
      icon: <ChartBar className="h-6 w-6 text-blue-600" />,
      title: "Kilpailukykyinen korko",
      description: "Vertaile pankkien tarjouksia ja löydä paras korko"
    },
    {
      icon: <Building2 className="h-6 w-6 text-blue-600" />,
      title: "Joustava vakuus",
      description: "Ostettava asunto käy vakuudeksi 70-75% arvostaan"
    },
    {
      icon: <FileText className="h-6 w-6 text-blue-600" />,
      title: "Selkeät ehdot",
      description: "Läpinäkyvät lainaehdot ja ei piilokustannuksia"
    }
  ];

  const kayttokohteet = [
    {
      title: "Kerrostaloasunnot",
      description: "Helppohoitoinen vaihtoehto kaupungissa",
      imagePath: "/lovable-uploads/d86acf72-a43e-4ce5-ad37-653f94156aa2.png",
      bgColor: "bg-blue-50"
    },
    {
      title: "Omakotitalot",
      description: "Oma piha ja enemmän tilaa",
      imagePath: "/lovable-uploads/2799a424-fa3c-4cfc-9232-be8134d3cb6f.png",
      bgColor: "bg-green-50"
    },
    {
      title: "Vapaa-ajan asunnot",
      description: "Mökki järven rannalla",
      imagePath: "/lovable-uploads/11ded48d-3847-4310-851e-6b4c8e16852b.png",
      bgColor: "bg-yellow-50"
    }
  ];

  return (
    <>
      <Helmet>
        <title>Asuntolaina | Lainasampo.fi</title>
        <meta name="description" content="Hae asuntolainaa Lainasampo.fi:n kautta ja vertaile eri pankkien tarjouksia. Löydä edullisin asuntolaina helposti!" />
      </Helmet>

      <div className="container mx-auto px-4 py-4">
        <Link 
          to="/lainatuotteet" 
          className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Takaisin lainatuotteisiin
        </Link>
      </div>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/lovable-uploads/848aa68b-46e3-4384-a7d5-fff22aea7921.png"
            alt="Moderni omakotitalo uima-altaalla"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
        </div>

        <div className="container relative z-10 mx-auto px-4 py-16 md:py-24">
          <div className="max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Asuntolaina unelmiesi kotiin
            </h1>
            <p className="text-xl text-blue-50 mb-8">
              Kilpailuta asuntolaina ilmaiseksi ja säästä jopa tuhansia euroja. 
              Vertaile pankkien tarjouksia ja löydä itsellesi sopivin vaihtoehto.
            </p>
            <LainaCta 
              to="https://go.adt253.net/t/t?a=1749114620&as=1854031059&t=2&tk=1" 
              text="Kilpailuta asuntolaina" 
              size="lg"
              isExternal={true}
              trackingLabel="asuntolaina-kilpailutus-hero"
            />
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <section className="mb-8">
              <div className="flex flex-col md:flex-row gap-6 items-center bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100 shadow-sm">
                <div className="md:w-1/4 flex justify-center">
                  <Image
                    src="/lovable-uploads/0b1d2b0b-29b3-4c60-a2c5-1b91ff908fac.png"
                    alt="Etua.fi logo"
                    width={200}
                    height={100}
                    className="mx-auto"
                    objectFit="contain"
                  />
                </div>
                <div className="md:w-3/4">
                  <h2 className="text-xl font-bold text-blue-800 mb-2">Kilpailuta asuntolainasi Etua.fi:n kautta</h2>
                  <p className="text-gray-700 mb-4">
                    Etua.fi:n avulla voit maksutta kilpailuttaa asuntolainasi vaivattomasti. Kilpailutuksella voit säästää jopa tuhansia euroja. Etua.fi on toiminut vuodesta 2008 ja on Suomen pisimpään toiminut lainavertailupalvelu.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Ilmainen palvelu</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Useita pankkeja</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Finanssivalvonnan hyväksymä</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild className="bg-blue-600 hover:bg-blue-700">
                      <a 
                        href="https://go.adt253.net/t/t?a=1749114620&as=1854031059&t=2&tk=1" 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        Kilpailuta asuntolainasi
                      </a>
                    </Button>
                    <Button asChild variant="outline" className="border-blue-500 text-blue-600">
                      <Link to="/lainat/remonttilaina">
                        Tarvitsetko remonttilainaa?
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </section>

            <section className="bg-gradient-to-br from-blue-50 to-purple-50 p-6 rounded-xl border border-blue-100 shadow-sm mb-8">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="md:w-1/4 flex justify-center">
                  <Image
                    src="/lovable-uploads/01da9111-1348-4bf2-bea4-df914538f8d3.png"
                    alt="Bluestep Bank logo"
                    width={200}
                    height={100}
                    className="mx-auto"
                    objectFit="contain"
                  />
                </div>
                <div className="md:w-3/4">
                  <h2 className="text-xl font-bold text-blue-800 mb-2">Bluestep Bank - Asuntolaina myös haastaviin tilanteisiin</h2>
                  <p className="text-gray-700 mb-4">
                    Eikö perinteinen pankki myöntänyt sinulle asuntolainaa? Bluestep Bank käsittelee jokaisen hakemuksen yksilöllisesti ja voi tarjota asuntolainaa, vaikka luottotiedoissasi olisi merkintöjä tai tulosi olisivat epäsäännölliset.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Joustava lainapäätös</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Yksilöllinen käsittely</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium">Jopa 85% vakuusarvo</span>
                    </div>
                  </div>
                  <Button asChild className="bg-blue-600 hover:bg-blue-700">
                    <a 
                      href="https://go.adt256.com/t/t?a=1540469708&as=1854031059&t=2&tk=1" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      Tutustu Bluestep Bankin asuntolainaan
                    </a>
                  </Button>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Asuntolainan edut
                </span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="border-blue-100">
                    <CardContent className="p-4 flex gap-4">
                      <div className="mt-1">{benefit.icon}</div>
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-1">{benefit.title}</h3>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
                  Asuntolainan laskuri
                </span>
              </h2>
              <AsuntolainaCalculator />
            </section>

            <section className="bg-white p-6 rounded-lg border border-blue-100">
              <h2 className="text-xl font-bold text-blue-800 mb-4 flex items-center gap-2">
                <InfoIcon className="h-5 w-5" />
                Mitä huomioida asuntolainassa?
              </h2>
              <ul className="space-y-3">
                <li className="flex gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-1" />
                  <span>Vertaile eri pankkien korkotarjouksia ja muita ehtoja huolellisesti</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-1" />
                  <span>Huomioi kokonaiskulut: korko, toimitusmaksu, tilinhoitomaksut</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-1" />
                  <span>Varmista että kuukausierä sopii budjettiisi pitkällä aikavälillä</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-1" />
                  <span>Tarkista mahdollisuus suojautua korkojen nousulta korkokatolla</span>
                </li>
                <li className="flex gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600 shrink-0 mt-1" />
                  <span>Selvitä lainan joustavuus: lyhennysvapaat, maksusuunnitelman muutokset</span>
                </li>
              </ul>
            </section>
          </div>

          <div className="space-y-6">
            <Card className="border-blue-100">
              <CardHeader>
                <CardTitle className="text-blue-800">Asuntolainan tiedot</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Lainasumma</span>
                  <span className="font-medium">10 000 € - 1 000 000 €</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Laina-aika</span>
                  <span className="font-medium">1 - 35 vuotta</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Korko alkaen</span>
                  <span className="font-medium">2.5%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Marginaali</span>
                  <span className="font-medium">0.45% - 2.0%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Toimitusmaksu</span>
                  <span className="font-medium">0 € - 600 €</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-blue-100 bg-gradient-to-br from-blue-50 to-purple-50">
              <CardHeader>
                <CardTitle className="text-blue-800">Mitä asuntolainalla voi rahoittaa?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Asunnon osto</h3>
                    <p className="text-sm text-gray-600">Uusi tai käytetty asunto omaan käyttöön</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Building2 className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Sijoitusasunto</h3>
                    <p className="text-sm text-gray-600">Asunto vuokrauskäyttöön</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Rakennusprojekti</h3>
                    <p className="text-sm text-gray-600">Uuden talon rakentaminen</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Home className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-gray-800">Asunnon vaihto</h3>
                    <p className="text-sm text-gray-600">Nykyisen asunnon vaihtaminen</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <LoanBenefitsSection />
          </div>
        </div>

        <section className="my-12">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-500">
              Asuntolainan käyttökohteet
            </span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {kayttokohteet.map((kohde, index) => (
              <KayttokohteetCard
                key={index}
                title={kohde.title}
                description={kohde.description}
                imagePath={kohde.imagePath}
                bgColor={kohde.bgColor}
              />
            ))}
          </div>
        </section>
        
        <section className="my-12">
          <div className="max-w-[1440px] mx-auto">
            <PartnersSection activeTab="pienlainat" />
          </div>
        </section>
      </div>
    </>
  );
};

export default AsuntolainaPage;
